import { logWarning } from './errorHandlers';

const CONTACT_RATE_LIMIT_STORAGE_KEY = "kickerContactRateLimit";
export const MIN_MS_BETWEEN_SENDS = 60_000;
export const MAX_SENDS_IN_WINDOW = 3;
export const RATE_WINDOW_MS = 15 * 60_000;

const DEFAULT_STATE = {
    lastSend: null,
    history: [],
};

const getStorage = () => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
        return null;
    }
    try {
        return window.localStorage;
    } catch (error) {
        logWarning("Contact rate limiter: unable to access localStorage", error);
        return null;
    }
};

const readState = (storage) => {
    if (!storage) {
        return { ...DEFAULT_STATE };
    }

    try {
        const raw = storage.getItem(CONTACT_RATE_LIMIT_STORAGE_KEY);
        if (!raw) {
            return { ...DEFAULT_STATE };
        }

        const parsed = JSON.parse(raw);
        const lastSend = typeof parsed.lastSend === "number" ? parsed.lastSend : null;
        const history = Array.isArray(parsed.history)
            ? parsed.history.filter((value) => typeof value === "number")
            : [];

        return {
            lastSend,
            history,
        };
    } catch (error) {
        logWarning("Contact rate limiter: failed to parse stored state", error);
        return { ...DEFAULT_STATE };
    }
};

const writeState = (storage, state) => {
    if (!storage) {
        return;
    }

    try {
        storage.setItem(
            CONTACT_RATE_LIMIT_STORAGE_KEY,
            JSON.stringify({ lastSend: state.lastSend, history: state.history })
        );
    } catch (error) {
        logWarning("Contact rate limiter: failed to persist state", error);
    }
};

const pruneHistory = (history, now) => history.filter((timestamp) => now - timestamp <= RATE_WINDOW_MS);

export const RateLimitReasons = {
    NONE: "none",
    TOO_SOON: "tooSoon",
    TOO_MANY: "tooMany",
};

export const checkAndRegisterSend = (now) => {
    const storage = getStorage();
    const state = readState(storage);
    const history = pruneHistory(state.history, now);
    const { lastSend } = state;

    const updatedState = {
        lastSend,
        history,
    };

    if (typeof lastSend === "number" && now - lastSend < MIN_MS_BETWEEN_SENDS) {
        writeState(storage, updatedState);
        return RateLimitReasons.TOO_SOON;
    }

    if (history.length >= MAX_SENDS_IN_WINDOW) {
        writeState(storage, updatedState);
        return RateLimitReasons.TOO_MANY;
    }

    const newHistory = [...history, now];
    const nextState = {
        lastSend: now,
        history: newHistory,
    };

    writeState(storage, nextState);
    return RateLimitReasons.NONE;
};

export const _resetContactRateLimiterState = () => {
    const storage = getStorage();
    if (!storage) {
        return;
    }

    try {
        storage.removeItem(CONTACT_RATE_LIMIT_STORAGE_KEY);
    } catch (error) {
        logWarning("Contact rate limiter: failed to reset state", error);
    }
};
